import React, {useEffect, useMemo, useState} from "react";
import {Link} from "react-router-dom";
import {fetchWeeklyLeaderboardAll} from "../lib/api";
import {formatLeaderboardDate} from "../lib/utils";
import {COMPETITION_ROWS, QUESTIONS_CATEGORIES} from "../lib/rankingConfig";
import {getWeeklyCache, hasWeeklyCache, setWeeklyCache} from "../state/leaderboardCache";
import {LeaderboardScore, CATEGORIES, ScoreCategory} from "../types/leaderboard";
import "../styles/ranking.css";

type CategoryState = {
  loading: boolean;
  error: string | null;
  scores: LeaderboardScore[];
  weekOf: string | null;
};

type DataByCategory = Record<ScoreCategory, CategoryState>;

const buildInitialState = (): DataByCategory => {
  const initial: Partial<DataByCategory> = {};
  (Object.keys(CATEGORIES) as ScoreCategory[]).forEach((key) => {
    initial[key] = {loading: true, error: null, scores: [], weekOf: null};
  });
  return initial as DataByCategory;
};

export const RankingPage: React.FC = () => {
  const [dataByCategory, setDataByCategory] = useState<DataByCategory>(() => getWeeklyCache<DataByCategory>() ?? buildInitialState());
  const isLoading = Object.values(dataByCategory).some((state) => state.loading);

  // Fetch all weekly leaderboards in ONE request
  useEffect(() => {
    if (hasWeeklyCache()) {
      return;
    }
    (async () => {
      try {
        const res = await fetchWeeklyLeaderboardAll();
        const {weekOf, scoresByCategory} = res;
        setDataByCategory((prev) => {
          const next: DataByCategory = { ...prev };
          (Object.keys(CATEGORIES) as ScoreCategory[]).forEach((category) => {
            const scores = scoresByCategory[category] || [];
            next[category] = {...prev[category], loading: false, error: null, scores, weekOf};
          });
          setWeeklyCache(next);
          return next;
        });
      } catch (err: any) {
        const message = err?.message || "Failed to load weekly leaderboard. See console for details.";

        setDataByCategory((prev) => {
          const next: DataByCategory = { ...prev };
          (Object.keys(CATEGORIES) as ScoreCategory[]).forEach((category) => {
            next[category] = {...prev[category], loading: false, error: message, scores: prev[category].scores, weekOf: prev[category].weekOf};
          });
          setWeeklyCache(next);
          return next;
        });
      }
    })();
  }, []);

  const globalWeekLabel = useMemo(() => {
    // Any non-null weekOf is fine; they're all the same from /weekly-all
    const anyWeek = (Object.keys(dataByCategory) as ScoreCategory[])
      .map((k) => dataByCategory[k].weekOf)
      .find((w): w is string => !!w);
    return anyWeek ? formatLeaderboardDate(anyWeek) : "";
  }, [dataByCategory]);

  const renderCategoryCard = (category: ScoreCategory, titleOverride?: string) => {
    const state = dataByCategory[category];
    const categoryTitle = titleOverride ?? CATEGORIES[category].label;

    if (!state.error && state.scores.length === 0) {
      return null;
    }

    return (
      <div className="category-card" key={category}>
        <h3 className="category-card-title">{categoryTitle}</h3>
        {state.error && <div className="error-text">Error: {state.error}</div>}
        {!state.error && (
          <div className="table-wrapper">
            <table className="ranking-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Player</th>
                  <th>Score</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {state.scores.length === 0 ? (<tr><td colSpan={4} className="muted-text">No scores yet.</td></tr>) : (state.scores.map((row, idx) => (
                    <tr key={`${row.user_id}-${row.at}-${idx}`}>
                      <td>{idx + 1}</td>
                      <td><Link to={`/profile/${encodeURIComponent(row.display_name)}`} className="player-link">{row.display_name}</Link></td>
                      <td>{row.score.toLocaleString()}</td>
                      <td>{formatLeaderboardDate(row.at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="ranking-page">
      <h1 className="page-title">Weekly Rankings</h1>
      {globalWeekLabel && <p className="week-label week-label-global">Week of {globalWeekLabel}</p>}
      <p className="page-subtitle">
        These rankings reset every week. For all-time rankings, see{" "}
        <Link to="/ranking-global" className="category-link">Global Ranking</Link>.
      </p>

      {isLoading && (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div className="muted-text">Loading weekly rankings...</div>
        </div>
      )}

      {!isLoading && (
        <>
          <section className="ranking-section">
            <div className="ranking-section-header"><h2>Timed Challenge</h2></div>
            <div className="competition-grid">
              {COMPETITION_ROWS.map((row) => {
                const visibleCards = row.categories
                  .map(({key, durationLabel}) => renderCategoryCard(key as ScoreCategory, `${row.label} - ${durationLabel}`))
                  .filter((card) => card !== null);
                
                if (visibleCards.length === 0) {
                  return null;
                }

                return (
                  <div className="competition-row" key={row.label}>
                    <h3 className="competition-row-label">{row.label}</h3>
                    <div className="competition-row-columns">{visibleCards}</div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="ranking-section">
            <div className="ranking-section-header"><h2>Race to 100 Questions</h2></div>
            <div className="questions-grid">{QUESTIONS_CATEGORIES.map((category) => renderCategoryCard(category))}</div>
          </section>
        </>
      )}
    </div>
  );
};

export default RankingPage;
